// contexts/NotificationContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { formatDistanceToNow } from "date-fns";

const NotificationContext = createContext();
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [error, setError] = useState(null);
  const socketRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const pingIntervalRef = useRef(null);
  const isMountedRef = useRef(false);
  const MAX_RECONNECT_ATTEMPTS = 5;
  const RECONNECT_DELAY = 5000;
  const getToken = useCallback(() => {
    return sessionStorage.getItem("accessToken")
  }, []);

  const getWebSocketUrl = useCallback(() => {
    const baseUrl = process.env.REACT_APP_WS_URL;
    const token = getToken();

    if (!token) {
      console.error("[NotificationContext] No token available for WebSocket connection");
      return null;
    }
    const url = `${baseUrl}?token=${encodeURIComponent(token)}`;
    return url;
  }, [getToken]);

  const updateUnreadCount = useCallback((notifs) => {
    const count = notifs.filter((n) => !n.is_read).length;
    setUnreadCount(count);
  }, []);

  const handleNewNotifications = useCallback(
    (data) => {
      if (!isMountedRef.current) return;
      const formattedNotifications = (Array.isArray(data) ? data : [data]).map(
        (notification) => ({
          ...notification,
          timeAgo: formatDistanceToNow(new Date(notification?.timestamp)),
          isNew: !notification.is_read,
        }),
      );

      setNotifications((prev) => {
        const newNotifications = formattedNotifications.filter(
          (newNotif) => !prev.some((existing) => existing.id === newNotif.id),
        );
        const merged = [...newNotifications, ...prev];

        return merged;
      });

      updateUnreadCount(formattedNotifications);
    },
    [updateUnreadCount],
  );

  const cleanupWebSocket = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    if (socketRef.current) {
      socketRef.current.onopen = null;
      socketRef.current.onmessage = null;
      socketRef.current.onerror = null;
      socketRef.current.onclose = null;

      if (socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.close();
      }

      socketRef.current = null;
    }
  }, []);

  const handleAuthenticationFailure = useCallback(() => {
    console.error("[NotificationContext] Authentication failed");
    setConnectionStatus("authentication_failed");
    setError("Authentication failed. Please log in again.");
    sessionStorage.removeItem("accessToken");
    sessionStorage.removeItem("token");
    localStorage.removeItem("access_token");
  }, []);

  const connectWebSocket = useCallback(() => {
    if (
      !isMountedRef.current ||
      reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS
    ) {
      return;
    }

    cleanupWebSocket();
    setConnectionStatus("connecting");

    const wsUrl = getWebSocketUrl();

    if (!wsUrl) {
      console.error("[NotificationContext] Cannot connect: No WebSocket URL");
      setConnectionStatus("error");
      return;
    }

    socketRef.current = new WebSocket(wsUrl);

    socketRef.current.onopen = () => {
      if (!isMountedRef.current) return;

      reconnectAttemptsRef.current = 0;
      setConnectionStatus("connected");
      setError(null);

      if (notifications.length === 0) {
        socketRef.current.send(
          JSON.stringify({ type: "request_notifications" }),
        );
      }
    };

    socketRef.current.onmessage = (event) => {
      if (!isMountedRef.current) return;

      try {
        const message = JSON.parse(event.data);

        switch (message.type) {
          case "notifications":
            handleNewNotifications(message.data);
            break;
          case "notification":
            handleNewNotifications(message.data);
            break;
          case "new_notification":
            handleNewNotifications(message.data);
            break;
          case "read_confirmation":
            if (message.success) {
              setNotifications((prev) =>
                prev.filter((n) => n.id !== message.id),
              );
              setUnreadCount((prev) => prev - 1);
            }
            break;
          case "initial_count":
            setUnreadCount(message.count);
            break;
          case "unread_count":
            setUnreadCount(message.count);
            break;
          case "heartbeat":
            if (socketRef.current?.readyState === WebSocket.OPEN) {
              socketRef.current.send(
                JSON.stringify({ type: "heartbeat", message: "pong" }),
              );
            }
            break;
          case "error":
            setError(message.message);
            console.error(
              "[NotificationContext] WebSocket error:",
              message.message,
            );
            break;
          default:
            console.warn(
              "[NotificationContext] Unknown message type:",
              message.type,
            );
            break;
        }
      } catch (err) {
        console.error("[NotificationContext] Error processing message:", err);
      }
    };

    socketRef.current.onerror = (error) => {
      if (!isMountedRef.current) return;
      setConnectionStatus("error");
      console.error("[NotificationContext] WebSocket error:", error);
    };

    socketRef.current.onclose = (event) => {
      if (!isMountedRef.current) return;

      console.log("[NotificationContext] WebSocket closed:", event.code, event.reason);

      // Handle authentication failure (code 4001)
      if (event.code === 4001) {
        handleAuthenticationFailure();
        return;
      }

      if (!event.wasClean) {
        setConnectionStatus("disconnected");
        reconnectAttemptsRef.current += 1;
        const delay = Math.min(
          RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current),
          60000,
        );
        console.warn(
          "[NotificationContext] WebSocket closed unexpectedly. Reconnecting in",
          delay,
          "ms",
        );
        setTimeout(connectWebSocket, delay);
      } else {
        console.log("[NotificationContext] WebSocket closed cleanly");
      }
    };
  }, [
    cleanupWebSocket,
    getWebSocketUrl,
    handleNewNotifications,
    notifications.length,
    handleAuthenticationFailure,
  ]);

  const markAsRead = useCallback((id) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(
        JSON.stringify({
          type: "mark_as_read",
          notification_id: id,
        }),
      );
    }
  }, []);

  const markAllAsRead = useCallback(() => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(
        JSON.stringify({
          type: "mark_all_read",
        }),
      );
    }
  }, []);

  const refreshNotifications = useCallback(() => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({ type: "request_notifications" }));
    }
  }, []);

  const reconnect = useCallback(() => {
    reconnectAttemptsRef.current = 0;
    connectWebSocket();
  }, [connectWebSocket]);

  useEffect(() => {
    isMountedRef.current = true;

    // Check if we have a token before connecting
    const token = getToken();
    if (token) {
      connectWebSocket();
    } else {
      console.warn("[NotificationContext] No token available, skipping WebSocket connection");
      setConnectionStatus("authentication_failed");
    }

    return () => {
      isMountedRef.current = false;
      cleanupWebSocket();
    };
  }, [getToken, connectWebSocket, cleanupWebSocket]);

  const value = {
    notifications,
    setNotifications,
    unreadCount,
    connectionStatus,
    error,
    markAsRead,
    markAllAsRead,
    refreshNotifications,
    reconnect,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider",
    );
  }
  return context;
};